import {isFirefox} from "#browser-utils/device-browser-detection"

/**
 * Creates an ImageBitmap from a cropped region of the source image.
 * Uses canvas as an intermediary to work around Firefox bug where crop parameters
 * are ignored when using createImageBitmap.
 * When the requested region extends outside the source bounds (e.g. because the
 * source has less margin than requested), the out-of-bounds area is zero-padded.
 */
export async function createCroppedImageBitmap(
    source: ImageBitmap | HTMLImageElement | HTMLCanvasElement | ImageData,
    sx: number,
    sy: number,
    sw: number,
    sh: number,
): Promise<ImageBitmap> {
    const sourceWidth = source instanceof ImageData ? source.width : source.width
    const sourceHeight = source instanceof ImageData ? source.height : source.height
    const needsPadding = sx < 0 || sy < 0 || sx + sw > sourceWidth || sy + sh > sourceHeight

    if (!needsPadding && !isFirefox) {
        return await createImageBitmap(source, sx, sy, sw, sh)
    }

    const canvas = new OffscreenCanvas(sw, sh)
    const ctx = canvas.getContext("2d") as OffscreenCanvasRenderingContext2D | null
    if (!ctx) {
        throw new Error("ThreeTiledTexture: failed to get 2D context for cropping")
    }

    let drawSource: ImageBitmap | HTMLImageElement | HTMLCanvasElement | OffscreenCanvas | null = null
    let releaseBitmap = false
    if (source instanceof ImageData) {
        drawSource = await createImageBitmap(source)
        releaseBitmap = true
    } else {
        drawSource = source
    }

    if (needsPadding) {
        // Canvas is zero-initialised; draw only the valid intersection with the source
        const srcX = Math.max(0, sx)
        const srcY = Math.max(0, sy)
        const srcW = Math.min(sourceWidth, sx + sw) - srcX
        const srcH = Math.min(sourceHeight, sy + sh) - srcY
        if (srcW > 0 && srcH > 0) {
            ctx.drawImage(drawSource as CanvasImageSource, srcX, srcY, srcW, srcH, srcX - sx, srcY - sy, srcW, srcH)
        }
    } else {
        // Firefox path: crop via canvas draw
        ctx.drawImage(drawSource as CanvasImageSource, sx, sy, sw, sh, 0, 0, sw, sh)
    }

    if (releaseBitmap) {
        ;(drawSource as ImageBitmap).close()
    }

    return await createImageBitmap(canvas as ImageBitmapSource)
}

/**
 * Generates the next mip level for the given ImageBitmap by downscaling it to half its size.
 * Handles odd dimensions by padding the image as necessary.
 * @param imageBitmap The source ImageBitmap to generate the mip level from.
 * @returns A Promise that resolves to the downscaled ImageBitmap.
 */
export const generateMipLevel = async (imageBitmap: ImageBitmap): Promise<ImageBitmap> => {
    const isOddX = (imageBitmap.width & 1) !== 0
    const isOddY = (imageBitmap.height & 1) !== 0
    const outWidth = Math.ceil(imageBitmap.width / 2)
    const outHeight = Math.ceil(imageBitmap.height / 2)
    const canvas = new OffscreenCanvas(outWidth, outHeight)
    const ctx = canvas.getContext("2d")
    if (!ctx) {
        throw new Error("Failed to get canvas context")
    }
    ctx.imageSmoothingQuality = "high"
    if (isOddX || isOddY) {
        const tempCanvas = new OffscreenCanvas(imageBitmap.width + (isOddX ? 1 : 0), imageBitmap.height + (isOddY ? 1 : 0))
        const tempCtx = tempCanvas.getContext("2d")
        if (!tempCtx) {
            throw new Error("Failed to get canvas context")
        }
        tempCtx.drawImage(imageBitmap, 0, 0)
        if (isOddX) {
            tempCtx.drawImage(imageBitmap, imageBitmap.width - 1, 0, 1, imageBitmap.height, imageBitmap.width, 0, 1, imageBitmap.height)
        }
        if (isOddY) {
            tempCtx.drawImage(imageBitmap, 0, imageBitmap.height - 1, imageBitmap.width, 1, 0, imageBitmap.height, imageBitmap.width, 1)
        }
        if (isOddX && isOddY) {
            tempCtx.drawImage(imageBitmap, imageBitmap.width - 1, imageBitmap.height - 1, 1, 1, imageBitmap.width, imageBitmap.height, 1, 1)
        }
        ctx.drawImage(tempCanvas, 0, 0, canvas.width, canvas.height)
    } else {
        ctx.drawImage(imageBitmap, 0, 0, canvas.width, canvas.height)
    }
    return createImageBitmap(canvas)
}
